/**
 * Centralized UI theme constants.
 * Single source of truth for colors, z-indices, and reusable style fragments.
 */

import themeTokens from './theme-tokens.json';

export const FONTS = themeTokens.fonts;
export const COLORS = themeTokens.colors;
export const GRADIENTS = themeTokens.gradients;
export const SHADOWS = themeTokens.shadows;
export const BLURS = themeTokens.blurs;
export const RADII = themeTokens.radii;
export const SPACE = themeTokens.spacing;
export const FONT_SIZES = themeTokens.fontSizes;
export const FONT_WEIGHTS = themeTokens.fontWeights;
export const LINE_HEIGHTS = themeTokens.lineHeights;
export const LETTER_SPACING = themeTokens.letterSpacings;
export const BORDER_WIDTHS = themeTokens.borderWidths;
export const DURATIONS = themeTokens.durations;
export const EASINGS = themeTokens.easings;
export const TRANSITIONS = themeTokens.transitions;
export const Z = themeTokens.zIndices;

// ── Font stack ──
export const FONT_FAMILY = FONTS.sans;
export const APP_TEXT_SCALE = 'var(--app-text-scale, 1)';

// ── Common touch / selection prevention (for all interactive overlays) ──
export const TOUCH_PROPS = `
  touch-action: manipulation;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
  -webkit-tap-highlight-color: transparent;
`;

export function scaledFontSize(fontSize: string): string {
  return `calc(${fontSize} * ${APP_TEXT_SCALE})`;
}

// ── Reusable style fragments ──

/** Full-screen overlay backdrop */
export function overlayStyle(zIndex: number, bg: string = COLORS.overlayBg): string {
  return `
    position: fixed;
    inset: 0;
    background: ${bg};
    display: flex;
    align-items: center;
    justify-content: center;
    padding: ${SPACE.s20};
    box-sizing: border-box;
    z-index: ${zIndex};
    font-family: ${FONT_FAMILY};
    ${TOUCH_PROPS}
  `;
}

/** Standard panel (card inside an overlay) */
export function panelStyle(width = '400px'): string {
  return `
    background: ${COLORS.whiteSoft};
    background-image: ${GRADIENTS.cardAccent};
    color: ${COLORS.black};
    border: ${BORDER_WIDTHS.medium} solid ${COLORS.black};
    border-radius: ${RADII.r12};
    padding: ${SPACE.s24};
    width: ${width};
    box-shadow: ${SHADOWS.panel};
    display: flex;
    flex-direction: column;
    gap: ${SPACE.s12};
    font-family: ${FONT_FAMILY};
  `;
}

/** Primary action button (brand gradient) */
export function primaryButtonStyle(): string {
  return `
    appearance: none;
    min-height: ${SPACE.s44};
    padding: ${SPACE.s10} ${SPACE.s16};
    border-radius: ${RADII.r8};
    border: ${BORDER_WIDTHS.medium} solid ${COLORS.black};
    background: ${GRADIENTS.brandPrimary};
    color: ${COLORS.black};
    font-weight: ${FONT_WEIGHTS.bold};
    letter-spacing: ${LETTER_SPACING.capsWide};
    text-transform: uppercase;
    text-decoration: none;
    cursor: pointer;
    box-shadow: ${SHADOWS.button};
    transition: ${TRANSITIONS.surfaceHover};
    ${TOUCH_PROPS}
  `;
}

/** Secondary / ghost button */
export function secondaryButtonStyle(): string {
  return `
    appearance: none;
    min-height: ${SPACE.s44};
    padding: ${SPACE.s10} ${SPACE.s16};
    border-radius: ${RADII.r8};
    border: ${BORDER_WIDTHS.medium} solid ${COLORS.black};
    background: ${COLORS.whiteSoft};
    color: ${COLORS.black};
    font-weight: ${FONT_WEIGHTS.bold};
    letter-spacing: ${LETTER_SPACING.capsWide};
    text-transform: uppercase;
    text-decoration: none;
    cursor: pointer;
    box-shadow: ${SHADOWS.buttonSoft};
    transition: ${TRANSITIONS.surfaceHover};
    ${TOUCH_PROPS}
  `;
}

/** Toggle button (collapse / expand) */
export function toggleButtonStyle(): string {
  return `
    appearance: none;
    background: ${COLORS.yellow};
    border: ${BORDER_WIDTHS.thin} solid ${COLORS.black};
    color: ${COLORS.black};
    font-size: ${FONT_SIZES.fs18};
    font-weight: ${FONT_WEIGHTS.bold};
    cursor: pointer;
    padding: ${SPACE.s0};
    width: ${SPACE.s24};
    height: ${SPACE.s24};
    box-shadow: ${SHADOWS.buttonSoft};
    ${TOUCH_PROPS}
  `;
}

/** Small floating panel (progress, dynamics) */
export function floatingPanelStyle(zIndex: number): string {
  return `
    position: fixed;
    background: ${COLORS.whiteSoft};
    background-image: ${GRADIENTS.cardAccent};
    color: ${COLORS.black};
    border: ${BORDER_WIDTHS.medium} solid ${COLORS.black};
    padding: ${SPACE.s14} ${SPACE.s16};
    border-radius: ${RADII.r10};
    width: 240px;
    z-index: ${zIndex};
    font-family: ${FONT_FAMILY};
    box-shadow: ${SHADOWS.floating};
    ${TOUCH_PROPS}
    transition: ${TRANSITIONS.fade};
  `;
}

/** Standard text input / select field */
export function inputControlStyle(
  options: {
    background?: string;
    borderColor?: string;
    radius?: string;
    padding?: string;
    fontSize?: string;
    width?: string;
  } = {}
): string {
  const {
    background = COLORS.whiteSoft,
    borderColor = COLORS.black,
    radius = RADII.r0,
    padding = `${SPACE.s12} ${SPACE.s14}`,
    fontSize = scaledFontSize(FONT_SIZES.fs14),
    width = '100%'
  } = options;

  return `
    appearance: none;
    width: ${width};
    padding: ${padding};
    background: ${background};
    border: ${BORDER_WIDTHS.medium} solid ${borderColor};
    border-radius: ${radius};
    color: ${COLORS.black};
    font-size: ${fontSize};
    font-weight: ${FONT_WEIGHTS.bold};
    font-family: ${FONT_FAMILY};
    outline: none;
    box-shadow: ${SHADOWS.buttonSoft};
  `;
}
